import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress, Box } from "@mui/material";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { setChartType } from "../../model/slice/chartTypeSlice";
import { GRAPH_TYPE } from "../../constants/trendGraph/trendGraphType";
import { ENGINE_CHART_TYPE } from "../../constants/trendGraph/enginesLine";
import { SEP_CHART_TYPE } from "../../constants/trendGraph/sepLines";
import { fetchVessel } from "../../api/vessel";
import {
  DEFAULT_CHART_TITLE,
  DEFAULT_CHART_TITLE_JP,
  DEFAULT_CUSTOM_CHART_KEY,
} from "../../constants/trendGraph/charts";
import { VESSEL_TYPE } from "../../constants/constants";
import { chartsData, isChartsFetching } from "../../model/slice/chartsSlice";
import { fetchChartsAsync } from "../../model/async/chartsAsync";
import { COLORS } from "../../constants/colors.js";
import { filterTabs } from "./trendPageCommonFunction.js";
import { DisplayTabSelector } from "./trendPageDisplayComponent/displayTabSelector.jsx";
import { DisplayChartsList } from "./trendPageDisplayComponent/displayChartsList.jsx";
import { DisplayGraph } from "./trendPageDisplayComponent/displayGraph.jsx";
import { DisplayCustomGraphPage } from "./trendPageDisplayComponent/displayCustomGraphPage.jsx";
import { ELECTRIC_TREND_TYPE } from "../../constants/trendGraph/electricLines.js";

const Trend = (props) => {
  const { vesselId, graph, chart } = useParams();
  const {
    selectedEngine, 
    setSelectedEngine, 
    isVesselOverview, 
    setChartRef,
    overviewTrendIdx, 
    selectedChartData, 
    channels 
  } = props;
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();;
  const charts = useSelector(chartsData);
  const isChartsFetch = useSelector(isChartsFetching);

  const [currentVessel, setCurrentVessel] = useState(null);
  const [selectedGraph, setSelectedGraph] = useState(graph ?? GRAPH_TYPE.ENGINE);
  const [customCharts, setCustomCharts] = useState(null);
  const [trendCharts, setTrendCharts] = useState(null);
  const [customChartTitle, setCustomChartTitle] = useState(DEFAULT_CHART_TITLE);
  const [isCustomChartsLoading, setIsCustomChartsLoading] = useState(false);
  const [isCreateCustomCharts, setIsCreateCustomCharts] = useState(false);
  const [isCustomChartsCreated, setIsCustomChartsCreated] = useState(false);
  const [isChartDeleted, setIsChartDeleted] = useState(false);
  const [isSepVessel, setIsSepVessel] = useState(graph === GRAPH_TYPE.SEP);
  const [isInitialLoad, setIsInitialLoad] = useState(false);

  const defaultChart = (value) => {
    const val = value ?? selectedGraph;
    switch (val) {
      case GRAPH_TYPE.CUSTOM:
        return customCharts?.[0]?.id;
      case GRAPH_TYPE.SEP:
        return SEP_CHART_TYPE.LEGS;
      case GRAPH_TYPE.ENGINE:
        return ENGINE_CHART_TYPE.ENGINE_LOAD;
      case GRAPH_TYPE.ELECTRIC:
        return ELECTRIC_TREND_TYPE.ELECTRIC_POWER_TREND;
    }
  };

  const confirmDefaultGraph = (isSEP) => {
    const filteredTab = filterTabs(isSEP, currentVessel?.NoME, currentVessel?.NoDG, currentVessel?.NoSG, t);
    const tmpGraph = filteredTab.map(f => f.id).includes(selectedGraph) ? selectedGraph : filteredTab?.[0]?.id;

    if (selectedGraph !== tmpGraph) {
      if (tmpGraph==GRAPH_TYPE.CUSTOM && !defaultChart(tmpGraph)) {
        navigate(`/trend/custom/custom/${vesselId}`, {replace: true});
      }
      else {
        navigate(`/trend/${tmpGraph}/${defaultChart(tmpGraph)}/${vesselId}`, {replace: true});
      }
    }
  };

  const [selectedChart, setSelectedChart] = useState(chart ?? defaultChart(graph));

  const updateSelectedChart = (selectedChart) => {
    setSelectedChart(selectedChart);
    dispatch(setChartType(selectedChart));
  };

  const fetchCustomCharts = async () => {
    setIsCustomChartsLoading(true);
    dispatch(fetchChartsAsync({ vesselId }));
  };

  const fetchTrendCharts = async () => {
    dispatch(fetchChartsAsync({ vesselId }));
  };

  const fetchCharts = () => {
    if (selectedGraph === GRAPH_TYPE.CUSTOM) {
      fetchCustomCharts();
    } else {
      fetchTrendCharts();
    }
  };

  useEffect(() => {
    if (!isChartsFetch) {
      if (selectedGraph === GRAPH_TYPE.CUSTOM) {
        const data = charts?.charts;
        setCustomCharts(data);
        setIsCustomChartsLoading(false);
        if (!data?.length) {
          setIsCreateCustomCharts(true);
          navigate(`/trend/${selectedGraph}/${DEFAULT_CUSTOM_CHART_KEY}/${vesselId}`);
        }
      } else {
        const data = charts?.trendCharts;
        setTrendCharts(data?.[selectedGraph]);
      }
    }
  }, [charts, isChartsFetch]);

  useEffect(() => {
    const fetchData = async () => {
      const data = (await fetchVessel(vesselId))?.vessel;
      setCurrentVessel(data);
    };
    fetchData();
  }, [vesselId]);

  useEffect(() => {
    if (!currentVessel) return;
    const isSEP = currentVessel.vesselType === VESSEL_TYPE.SEP
    setIsSepVessel(isSEP);
    confirmDefaultGraph(isSEP);
    localStorage.setItem('isSepVessel', JSON.stringify(isSEP));
  }, [currentVessel]);
  
  useEffect(() => {
    const isManualRefresh = sessionStorage.getItem('isManualRefresh') === 'true';
    sessionStorage.setItem('isManualRefresh', 'false');
  
    if (isManualRefresh) {
      const storedVesselType = localStorage.getItem('isSepVessel');
      const isSEP = JSON.parse(storedVesselType);
      const graphType = isSEP ? GRAPH_TYPE.SEP : GRAPH_TYPE.ENGINE;
  
      navigate(`/trend/${graphType}/${defaultChart(graphType)}/${vesselId}`);
    }
  }, [isSepVessel, currentVessel]);
  
  useEffect(() => {
    setSelectedGraph(
      [GRAPH_TYPE.ENGINE, GRAPH_TYPE.SEP].includes(graph)
        ? isSepVessel
          ? GRAPH_TYPE.SEP
          : GRAPH_TYPE.ENGINE
        : graph
    );
  }, [currentVessel, isSepVessel]);
  
  useEffect(() => {
    if (isCustomChartsCreated) {
      fetchCustomCharts();
    }
  }, [isCustomChartsCreated]);

  useEffect(() => {
    if (isChartDeleted) {
      fetchCustomCharts();
    }
  }, [isChartDeleted]);

  useEffect(() => {
    if (selectedGraph !== GRAPH_TYPE.CUSTOM) return;
    if (!isCustomChartsLoading) {
      if (isCustomChartsCreated) {
        setIsCustomChartsCreated(false);
        if (selectedChart === DEFAULT_CUSTOM_CHART_KEY) {
          updateSelectedChart(customCharts[customCharts.length - 1].id);
        }
        setCustomChartTitle(DEFAULT_CHART_TITLE);
      } else {
        if (customCharts?.length > 0 && !isCreateCustomCharts) {
          if (isInitialLoad) {
            setIsInitialLoad(false);
          } else {
            // selectedChart doesn't update when change the selectedVessel by vesselSelector.
            const checkValue = customCharts.some((v) => v?.id === selectedChart)
              ? selectedChart
              : "undefined";
            const newSelectedChart =
              checkValue === "undefined" || checkValue === DEFAULT_CUSTOM_CHART_KEY
                ? defaultChart()
                : checkValue;
            if (
              selectedGraph === GRAPH_TYPE.CUSTOM &&
              (checkValue === "undefined" || checkValue === DEFAULT_CUSTOM_CHART_KEY)
            ) {
              // to prevent back to /undefined/~
              navigate(`/trend/${selectedGraph}/${newSelectedChart}/${vesselId}`, {replace: true});
            }
            updateSelectedChart(newSelectedChart);
          }
        } else if (customCharts?.length > 0 && isChartDeleted) {
          updateSelectedChart(defaultChart());
          setIsChartDeleted(false);
        } else {
          if (!isCreateCustomCharts) return;
          updateSelectedChart(DEFAULT_CUSTOM_CHART_KEY);
          navigate(`/trend/${selectedGraph}/${DEFAULT_CUSTOM_CHART_KEY}/${vesselId}`);
          setIsCreateCustomCharts(true);
        }
      }
    }
  }, [customCharts, isInitialLoad]);

  useEffect(() => {
    setSelectedGraph(graph);
  }, [graph]);

  useEffect(() => {
    dispatch(setChartType(selectedChart));
    fetchCharts();
  }, [selectedChart]);

  useEffect(() => {
    setIsCreateCustomCharts(false);
    fetchCharts();
    if (selectedGraph === GRAPH_TYPE.CUSTOM) {
      setSelectedChart(defaultChart());
    }
  }, [vesselId, selectedGraph]);

  useEffect(() => {
    if (chart === "undefined" && selectedGraph === GRAPH_TYPE.CUSTOM) {
      navigate(`/trend/${selectedGraph}/${DEFAULT_CUSTOM_CHART_KEY}/${vesselId}`);
    } else {
      setSelectedChart(chart);
      if (selectedGraph === GRAPH_TYPE.CUSTOM && chart === DEFAULT_CUSTOM_CHART_KEY) {
        setIsCreateCustomCharts(true);
      }
    }
  }, [chart]);

  useEffect(() => {
    switch (i18n.language) {
      case "en-US":
        if (customChartTitle === DEFAULT_CHART_TITLE_JP) {
          setCustomChartTitle(DEFAULT_CHART_TITLE);
        }
        break;
      case "ja":
        if (customChartTitle === DEFAULT_CHART_TITLE) {
          setCustomChartTitle(DEFAULT_CHART_TITLE_JP);
        }
        break;
    }
  }, [i18n.language]);

  useEffect(() => {
    setIsInitialLoad(true);

    return () => {
      dispatch(setChartType(null));
    };
  }, []);

  if(currentVessel == null){
    return (
      <Box component={"div"} sx={{display: 'flex', width: '100%', height: '80vh', alignItems: 'center', justifyContent: 'center'}}>
        <CircularProgress/>
      </Box>
    );
  }

  return (
    <div>
      <DisplayTabSelector
        vesselId={vesselId} 
        currentVessel={currentVessel}
        selectedGraph={selectedGraph}
        isSepVessel={isSepVessel}
        defaultChart={defaultChart}
      />
      <Box component={"div"} sx={{
        height: "50px",
        width: "100%",
        position: "fixed",
        top: "98px",
        display: "flex",
        backgroundColor: COLORS.backgroundColor,
        zIndex: 999,
      }}>
      <DisplayChartsList
        vesselId={vesselId}
        isSepVessel={isSepVessel}
        customCharts={customCharts}
        currentVessel={currentVessel}
        selectedGraph={selectedGraph}
        selectedChart={selectedChart}
        setSelectedChart={updateSelectedChart}
        isCreateCustomCharts={isCreateCustomCharts}
        setIsCreateCustomCharts={setIsCreateCustomCharts}
        customChartTitle={customChartTitle}
      />
      </Box>
      {selectedGraph === GRAPH_TYPE.CUSTOM ? (
        <DisplayCustomGraphPage
          vesselId={vesselId}
          selectedEngine={selectedEngine}
          setSelectedEngine={setSelectedEngine}
          trendCharts={trendCharts}
          isVesselOverview={isVesselOverview}
          setChartRef={setChartRef}
          overviewTrendIdx={overviewTrendIdx}
          selectedGraph={selectedGraph}
          currentVessel={currentVessel}
          customChartTitle={customChartTitle}
          setCustomChartTitle={setCustomChartTitle}
          customCharts={customCharts}
          selectedChart={selectedChart}
          isSepVessel={isSepVessel}
          isCustomChartsCreated={isCustomChartsCreated}
          setIsCustomChartsCreated={setIsCustomChartsCreated}
          setIsChartDeleted={setIsChartDeleted}
          isCreateCustomCharts={isCreateCustomCharts}
          setIsCreateCustomCharts={setIsCreateCustomCharts}
        />
      ) : (
        <DisplayGraph
          vesselId={vesselId}
          selectedChartData={selectedChartData}
          setIsCreateCustomCharts={setIsCreateCustomCharts}
          isVesselOverview={isVesselOverview}
          setChartRef={setChartRef}
          customCharts={customCharts}
          channels={channels}
          isCustomChartsCreated={isCreateCustomCharts}
          overviewTrendIdx={overviewTrendIdx}
          selectedGraph={selectedGraph}
          selectedChart={selectedChart}
          isSepVessel={isSepVessel}
          currentVessel={currentVessel}
          trendCharts={trendCharts}
        />
      )}
    </div>
  );
};

// Actually, Trend props doesn't have these value but child component use these value, we need to modify them later.
Trend.propTypes = {
  selectedEngine: PropTypes.string,
  setSelectedEngine: PropTypes.func, 
  isVesselOverview: PropTypes.bool, 
  setChartRef: PropTypes.func,
  overviewTrendIdx: PropTypes.number, 
  selectedChartData: PropTypes.array, 
  channels: PropTypes.array 
};

export default Trend;
